"use client";

import React, {
	createContext,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from "react";
import { useSearchParams } from "next/navigation";
import {
	ActiveFiltersView,
	SearchFiltersView,
} from "@/app/listing/models/filters";
import { useFilters } from "@/app/listing/hooks/use-filters";
import { usePersistedListingFilters } from "@/app/listing/hooks/use-persisted-listing-filters";
import { useUserContext } from "@/app/shared/user";
import {
	getEffectiveCountryCodes,
	isEuMarketUser,
} from "@/app/listing/utils/eu-markets";

const LOCATION_FILTER_STORAGE_KEY = "infinit-listing-location-filter";

interface FilterContextType {
	filters: SearchFiltersView | null;
	activeFilters: ActiveFiltersView;
	isLoading: boolean;
	error: Error | null;
	updateFilters: (
		newFilters:
			| Partial<ActiveFiltersView>
			| ((prev: ActiveFiltersView) => ActiveFiltersView)
	) => void;
	resetFilters: () => void;
	refreshFilters: () => Promise<SearchFiltersView | undefined>;
	countryCodes: string[] | undefined;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export function FilterContextProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const userContext = useUserContext();
	const countryCode = userContext?.traits?.countryCode;
	const searchParams = useSearchParams();
	const hasRestoredFromStorage = useRef(false);
	const isEuUser = isEuMarketUser(countryCode);

	// Compute effective countryCodes for all EU markets (used for search parameters).
	// Search parameters always use all EU markets so users can see all available filter options.
	// Non-EU users get undefined to use single-market behavior.
	const allEuCountryCodes = useMemo(
		() => getEffectiveCountryCodes(countryCode),
		[countryCode]
	);

	// Use the existing filters hook - pass all EU country codes for search parameters
	const {
		filters,
		activeFilters,
		isLoading,
		error,
		updateFilters,
		resetFilters,
		refreshFilters,
	} = useFilters(countryCode, allEuCountryCodes);

	usePersistedListingFilters(activeFilters, updateFilters);

	// Restore saved location from localStorage on mount (EU market users only).
	// URL params take precedence - only restore if no markets param is in the URL.
	useEffect(() => {
		if (!isEuUser || hasRestoredFromStorage.current) return;
		hasRestoredFromStorage.current = true;

		// If URL already has markets param, don't override with localStorage
		if (searchParams.get("markets")) return;

		try {
			const stored = localStorage.getItem(LOCATION_FILTER_STORAGE_KEY);
			if (stored) {
				const parsed = JSON.parse(stored);
				if (Array.isArray(parsed)) {
					const savedMarkets = parsed
						.filter((value): value is string => typeof value === "string")
						.filter(market =>
							Array.isArray(allEuCountryCodes)
								? allEuCountryCodes.includes(market)
								: true
						);
					if (savedMarkets.length > 0) {
						updateFilters({ markets: savedMarkets });
					}
				}
			}
		} catch {
			// Silently handle localStorage errors (e.g., private browsing mode)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isEuUser]);

	// Persist location selection to localStorage when it changes (EU market users only).
	// Skip persistence until after the restore attempt completes to avoid briefly clearing saved data.
	useEffect(() => {
		if (!isEuUser || !hasRestoredFromStorage.current) return;

		try {
			if (activeFilters.markets.length > 0) {
				localStorage.setItem(
					LOCATION_FILTER_STORAGE_KEY,
					JSON.stringify(activeFilters.markets)
				);
			} else {
				localStorage.removeItem(LOCATION_FILTER_STORAGE_KEY);
			}
		} catch {
			// Silently handle localStorage errors (e.g., private browsing mode)
		}
	}, [activeFilters.markets, isEuUser]);

	// Compute effective countryCodes based on active market filters for deal fetching.
	// Non-EU users get undefined to use single-market behavior.
	const countryCodes = useMemo(
		() => getEffectiveCountryCodes(countryCode, activeFilters.markets),
		[countryCode, activeFilters.markets]
	);

	const contextValue: FilterContextType = {
		filters,
		activeFilters,
		isLoading,
		error,
		updateFilters,
		resetFilters,
		refreshFilters,
		countryCodes,
	};

	return (
		<FilterContext.Provider value={contextValue}>
			{children}
		</FilterContext.Provider>
	);
}

export function useFilterContext() {
	const context = useContext(FilterContext);
	if (!context) {
		throw new Error(
			"useFilterContext must be used within a FilterContextProvider"
		);
	}
	return context;
}
